/**
 * Bold — "Buy set" CTA.
 *
 * Drives <bundle-item-selector> from outside it. The selector owns selection
 * state and publishes `bundle:change` (availability, totals) and `bundle:error`;
 * this element only reflects that and calls addSetToCart(button).
 *
 * The button ships disabled and is enabled only once every row of the set has an
 * available variant chosen - the selector's `selection` getter returns an empty
 * array until then, so a partial set can never be added.
 */
(() => {
  class BoldBuySet extends HTMLElement {
    connectedCallback() {
      this.btn = this.querySelector('[data-bold-buy-set-btn]');
      this.label = this.querySelector('[data-bold-buy-set-label]');
      this.priceWrap = this.querySelector('[data-bold-buy-set-price]');
      this.totalEl = this.querySelector('[data-bold-buy-set-total]');
      this.compareEl = this.querySelector('[data-bold-buy-set-compare]');
      this.stockText = this.querySelector('[data-bold-buy-set-stock-text]');
      this.errorEl = this.querySelector('[data-bold-buy-set-error]');

      this.btn?.addEventListener('click', () => {
        const selector = this.selector();
        if (selector && typeof selector.addSetToCart === 'function') {
          if (this.errorEl) this.errorEl.hidden = true;
          selector.addSetToCart(this.btn);
        }
      });

      // The selector may upgrade after this element, so listen on document and
      // ask for the current state once, rather than assuming an order.
      document.addEventListener('bundle:change', e => this.render(e.detail));
      document.addEventListener('bundle:error', e => this.showError(e.detail?.message));
      requestAnimationFrame(() => this.pull());
    }

    selector() {
      return document.querySelector('bundle-item-selector');
    }

    /* Nothing has fired yet on a cold load, so read the state directly. */
    pull() {
      const selector = this.selector();
      if (!selector) return;
      const ready = Array.isArray(selector.selection) && selector.selection.length > 0;
      this.render({ allAvailable: ready });
    }

    money(cents) {
      const selector = this.selector();
      const format = selector?.dataset.moneyFormat || '${{amount}}';
      const amount = cents % 100 === 0 ? String(cents / 100) : (cents / 100).toFixed(2);
      return format.replace(/\{\{\s*amount[^}]*\}\}/, amount);
    }

    render(detail = {}) {
      const { allAvailable, total, compareTotal } = detail;
      if (this.btn) this.btn.disabled = !allAvailable;
      /* Derived, not configured: the wording follows whether every selected
         variant is available, so there are no labels to keep in sync. */
      if (this.stockText) {
        this.stockText.textContent = allAvailable
          ? 'In stock \u2014 ready to ship'
          : 'Select an available option for each item';
      }
      this.classList.toggle('is-unavailable', !allAvailable);
      if (this.priceWrap && typeof total === 'number') {
        this.priceWrap.hidden = !allAvailable;
        if (this.totalEl) this.totalEl.textContent = this.money(Math.round(total));
        if (this.compareEl) {
          const show = typeof compareTotal === 'number' && compareTotal > total;
          this.compareEl.hidden = !show;
          if (show) this.compareEl.textContent = this.money(Math.round(compareTotal));
        }
      }
    }

    showError(message) {
      if (!this.errorEl || !message) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }
  }

  if (!customElements.get('bold-buy-set')) customElements.define('bold-buy-set', BoldBuySet);
})();
